/**
 * Passphrase encryption for a locked entry's content, byte-compatible with the desktop's
 * `security.ContentCipher`: PBKDF2-HMAC-SHA256 (210,000 iterations, UTF-8 passphrase,
 * 16-byte salt) derives an AES-256-GCM key; the stored value is base64 of
 * salt | 12-byte IV | ciphertext with its 128-bit tag. Shared vectors in
 * `schema/fixtures/content-cipher-vectors.txt` keep the two editions in step.
 */
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const PBKDF2_ITERATIONS = 210_000;

/** A wrong passphrase or a damaged blob - indistinguishable by design, as on the desktop. */
export class IncorrectPassphraseError extends Error {
  constructor() {
    super('Incorrect passphrase, or the locked content is damaged.');
    this.name = 'IncorrectPassphraseError';
  }
}

async function deriveKey(passphrase: string, salt: Uint8Array<ArrayBuffer>, usage: KeyUsage): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage],
  );
}

export async function encryptContent(plaintext: string, passphrase: string): Promise<string> {
  return encryptContentWith(plaintext, passphrase, crypto.getRandomValues(new Uint8Array(SALT_LENGTH)), crypto.getRandomValues(new Uint8Array(IV_LENGTH)));
}

/**
 * `encryptContent` with a given salt and IV, for fixtures that must come out the same
 * each time they're built (testing/webWrittenRealm.ts). Real locks use `encryptContent`:
 * an IV must never be reused with the same key.
 */
export async function encryptContentWith(
  plaintext: string,
  passphrase: string,
  salt: Uint8Array<ArrayBuffer>,
  iv: Uint8Array<ArrayBuffer>,
): Promise<string> {
  if (salt.byteLength !== SALT_LENGTH || iv.byteLength !== IV_LENGTH) throw new Error('A salt is 16 bytes and an IV 12.');
  const key = await deriveKey(passphrase, salt, 'encrypt');
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, key, new TextEncoder().encode(plaintext)),
  );
  const blob = new Uint8Array(SALT_LENGTH + IV_LENGTH + ciphertext.byteLength);
  blob.set(salt, 0);
  blob.set(iv, SALT_LENGTH);
  blob.set(ciphertext, SALT_LENGTH + IV_LENGTH);
  return toBase64(blob);
}

export async function decryptContent(base64Blob: string, passphrase: string): Promise<string> {
  let blob: Uint8Array<ArrayBuffer>;
  try {
    blob = fromBase64(base64Blob);
  } catch {
    throw new IncorrectPassphraseError();
  }
  if (blob.byteLength < SALT_LENGTH + IV_LENGTH) {
    throw new IncorrectPassphraseError();
  }
  const salt = blob.slice(0, SALT_LENGTH);
  const iv = blob.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  try {
    const key = await deriveKey(passphrase, salt, 'decrypt');
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      key,
      blob.slice(SALT_LENGTH + IV_LENGTH),
    );
    return new TextDecoder('utf-8', { fatal: true }).decode(plaintext);
  } catch {
    throw new IncorrectPassphraseError();
  }
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  // Java's basic decoder rejects anything outside the alphabet, so this does too.
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(value) || value.length % 4 !== 0) {
    throw new Error('not base64');
  }
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
